import { useCallback, useEffect, useState } from 'react';
import {
  readSavedThemePreference,
  readSystemTheme,
  resolveThemePreference,
  subscribeToSavedThemePreference,
  subscribeToSystemTheme,
  writeSavedThemePreference,
  type ThemePreference,
  type ThemeType,
} from '@wavvest/shared-layout';

export type AuthColorScheme = ThemeType;

const SCHEME_OVERRIDE_PARAM = 'theme';

const readSchemeOverride = (): AuthColorScheme | undefined => {
  const requested = new URLSearchParams(window.location.search).get(SCHEME_OVERRIDE_PARAM);
  return requested === 'light' || requested === 'dark' ? requested : undefined;
};

export const useAuthTheme = () => {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(
    () => readSavedThemePreference() ?? 'system',
  );
  const [systemTheme, setSystemTheme] = useState<AuthColorScheme>(() => readSystemTheme());

  useEffect(
    () =>
      subscribeToSavedThemePreference(() => {
        setThemePreferenceState(readSavedThemePreference() ?? 'system');
      }),
    [],
  );
  useEffect(() => subscribeToSystemTheme(() => setSystemTheme(readSystemTheme())), []);

  const setThemePreference = useCallback((preference: ThemePreference) => {
    setThemePreferenceState(preference);
    writeSavedThemePreference(preference);
  }, []);

  return {
    colorScheme: readSchemeOverride() ?? resolveThemePreference(themePreference, systemTheme),
    setThemePreference,
    themePreference,
  };
};

/**
 * The effective color scheme for pre-auth screens. Kept as a small adapter for
 * consumers that only need the resolved light/dark value.
 */
export const useAuthColorScheme = (): AuthColorScheme => useAuthTheme().colorScheme;
